// @flow

import React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';

import { urlWithNext } from '@zapier/url-utils';

import Link from 'app/common/components/Link';
import LoginActions from 'app/login/LoginActions';
import LoginDifferent from 'app/login/components/LoginDifferent';
import LoginForm from 'app/login/components/LoginForm';
import { formattedMessageForResult } from 'app/login/LoginUtils';
import { getQueryParam } from 'app/common/utils/RouterSelectors';
import { isValidCode } from 'app/login/submitResults';

const Description = ({ useRecoveryCode }: { useRecoveryCode: boolean }) => {
  const description = useRecoveryCode
    ? 'Enter a recovery code that you saved when setting up 2FA.'
    : 'Enter the 6-digit code generated by your authentication app.';
  return <p>{description}</p>;
};

const Footer = ({
  useRecoveryCode,
  next,
}: {
  useRecoveryCode: boolean,
  next: string,
}) => {
  const href = useRecoveryCode
    ? '/app/login/2fa'
    : '/app/login/2fa?use_recovery_code=yes';
  const message = useRecoveryCode ? 'Use a 2FA code' : 'Use a recovery code';
  return (
    <div>
      <p>
        <Link href={urlWithNext(href, next)}>{message}</Link>
      </p>
      <LoginDifferent next={next} />
    </div>
  );
};

const mapStateToProps = state => {
  const page = state.login.twoFactorAuthPage;
  const next = state.router.current.query.next;

  // code is required
  const canSubmit =
    !page.isSubmitting && !!(page.form.code || page.form.recovery_code);

  // various backend views follow this ?use_recovery_code=yes querystring convention
  const useRecoveryCode = getQueryParam(state, 'use_recovery_code') === 'yes';

  const fields = useRecoveryCode
    ? [
        {
          name: 'recovery_code',
          type: 'text',
          placeholder: 'Recovery Code',
          autoFocus: true,
          value: page.form.recovery_code,
          autoComplete: 'off',
        },
      ]
    : [
        {
          name: 'code',
          type: 'text',
          placeholder: 'Authentication Code',
          autoFocus: true,
          value: page.form.code,
          autoComplete: 'off',
        },
      ];

  const message = formattedMessageForResult(page.result, {
    isUsingRecoveryCode: useRecoveryCode,
  });

  return {
    title: 'Two-Factor Authentication',
    submitText: 'Confirm',
    canSubmit,
    didSucceed: isValidCode(page.result),
    isSubmitting: page.isSubmitting,
    fields,
    message: message.text,
    messageType: message.type,
    description: <Description useRecoveryCode={useRecoveryCode} />,
    footer: <Footer next={next} useRecoveryCode={useRecoveryCode} />,
    shouldShowLoginForm: true,
    next,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    onChange: (value, name) => {
      dispatch(LoginActions.updateForm('twoFactorAuthPage', name, value));
    },
    onSubmit: (fields, next) => {
      const field = _.first(fields);
      dispatch(LoginActions.confirm2FACode(field.name, field.value, next));
    },
  };
};

const TwoFactorAuthFormContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(LoginForm);

export default TwoFactorAuthFormContainer;
